/**
 * Tag Editor Component
 *
 * Reusable inline tag management: shows assigned tags with remove buttons,
 * plus a searchable dropdown to add existing tags or create new ones.
 */

import { useState, useRef, useEffect } from 'react';
import { Plus, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import type { TagDefinition } from '../types/image-generator.types';

export interface TagEditorProps {
  assignedTagIds: string[];
  tagDefinitions: TagDefinition[];
  onAssignTag: (tagId: string) => void;
  onUnassignTag: (tagId: string) => void;
  onCreateTag: (name: string) => string;
}

export function TagEditor({
  assignedTagIds,
  tagDefinitions,
  onAssignTag,
  onUnassignTag,
  onCreateTag,
}: TagEditorProps) {
  const [showDropdown, setShowDropdown] = useState(false);
  const [search, setSearch] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);

  const assignedTags = tagDefinitions.filter((t) => assignedTagIds.includes(t.id));
  const availableTags = tagDefinitions.filter((t) => !assignedTagIds.includes(t.id));

  const filteredAvailableTags = search.trim()
    ? availableTags.filter((t) => t.name.toLowerCase().includes(search.toLowerCase()))
    : availableTags;

  const exactMatch = tagDefinitions.find(
    (t) => t.name.toLowerCase() === search.trim().toLowerCase(),
  );
  const showCreateOption = search.trim() && !exactMatch;
  const isAlreadyApplied = exactMatch && assignedTagIds.includes(exactMatch.id);

  // Close dropdown on outside click
  useEffect(() => {
    if (!showDropdown) return;
    function handleClickOutside(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
        setSearch('');
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showDropdown]);

  const handleAssign = (tagId: string) => {
    if (!assignedTagIds.includes(tagId)) {
      onAssignTag(tagId);
    }
    setSearch('');
    setShowDropdown(false);
  };

  const handleCreateAndAssign = () => {
    const trimmed = search.trim();
    if (!trimmed) return;
    const newTagId = onCreateTag(trimmed);
    onAssignTag(newTagId);
    setSearch('');
    setShowDropdown(false);
  };

  return (
    <div>
      {/* Assigned tags with remove buttons */}
      {assignedTags.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-2">
          {assignedTags.map((tag) => (
            <Badge key={tag.id} variant="secondary" className="text-xs gap-1">
              {tag.name}
              <button
                type="button"
                onClick={() => onUnassignTag(tag.id)}
                className="ml-0.5 hover:text-destructive"
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
        </div>
      )}

      {/* Add a tag button + dropdown */}
      <div ref={dropdownRef} className="relative">
        {!showDropdown ? (
          <Button
            variant="ghost"
            size="sm"
            className="h-7 text-xs gap-1 text-muted-foreground hover:text-primary px-2"
            onClick={() => setShowDropdown(true)}
          >
            <Plus className="h-3 w-3" />
            Add a tag
          </Button>
        ) : (
          <>
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  e.stopPropagation();
                  const trimmed = search.trim();
                  if (!trimmed) return;
                  if (exactMatch) {
                    if (!assignedTagIds.includes(exactMatch.id)) {
                      handleAssign(exactMatch.id);
                    }
                  } else {
                    handleCreateAndAssign();
                  }
                }
                if (e.key === 'Escape') {
                  e.stopPropagation();
                  setShowDropdown(false);
                  setSearch('');
                }
              }}
              placeholder="Search or create tag..."
              className="h-8 text-xs"
              autoFocus
            />
            {(filteredAvailableTags.length > 0 || showCreateOption || isAlreadyApplied) && (
              <div className="absolute z-50 w-full mt-1 bg-popover border border-border rounded-md shadow-md max-h-40 overflow-y-auto">
                {filteredAvailableTags.map((tag) => (
                  <button
                    key={tag.id}
                    type="button"
                    onClick={() => handleAssign(tag.id)}
                    className="w-full text-left px-3 py-1.5 text-xs hover:bg-muted transition-colors"
                  >
                    {tag.name}
                  </button>
                ))}
                {isAlreadyApplied && filteredAvailableTags.length === 0 && (
                  <span className="block px-3 py-1.5 text-xs text-muted-foreground">
                    Tag already applied
                  </span>
                )}
                {showCreateOption && (
                  <button
                    type="button"
                    onClick={handleCreateAndAssign}
                    className="w-full text-left px-3 py-1.5 text-xs text-primary hover:bg-muted transition-colors border-t border-border"
                  >
                    Create &quot;{search.trim()}&quot;
                  </button>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
